import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { ChartBar, Package, Users, SimCard, ShoppingCart, Globe, Lightning, GearSix, Gift, UsersThree, CurrencyBtc, EnvelopeSimple } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import SimCardIcon from "../../components/SimCardIcon";

export default function AdminLayout() {
  const { t } = useTranslation();
  const { user, logout, loading } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  if (loading) return <div className="p-12 text-ink-500">{t("common.loading")}</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const links = [
    { to: "/admin", icon: <ChartBar size={18} />, label: t("admin.stats"), end: true },
    { to: "/admin/orders", icon: <ShoppingCart size={18} />, label: t("admin.orders") },
    { to: "/admin/customers", icon: <Users size={18} />, label: t("admin.customers") },
    { to: "/admin/esims", icon: <SimCard size={18} />, label: t("admin.esims") },
    { to: "/admin/plans", icon: <Package size={18} />, label: t("admin.catalog") },
    { to: "/admin/webhooks", icon: <Lightning size={18} />, label: t("admin.webhooks", "Webhooks") },
    { to: "/admin/gifts", icon: <Gift size={18} />, label: t("admin.gifts", "Gift eSIMs") },
    { to: "/admin/crypto", icon: <CurrencyBtc size={18} />, label: t("admin.crypto", "Crypto Deposits") },
    { to: "/admin/emails", icon: <EnvelopeSimple size={18} />, label: t("admin.emails", "Emails") },
    { to: "/admin/team", icon: <UsersThree size={18} />, label: t("admin.team", "Team") },
    { to: "/admin/settings", icon: <GearSix size={18} />, label: t("admin.settings", "Settings") },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="admin-layout">
      <header className="border-b border-ink-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-cobalt-500 rounded-full flex items-center justify-center"><SimCardIcon size={15} className="text-white" /></div>
            <span className="font-display font-bold tracking-tight">BlueRoam · {t("admin.title")}</span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="text-ink-500">{user.email}</span>
            <button onClick={() => { logout(); nav("/"); }} className="text-cobalt-600 hover:underline font-semibold" data-testid="admin-logout">{t("common.logout")}</button>
          </div>
        </div>
      </header>
      <div className="flex-1 flex">
        <aside className="w-56 border-r border-ink-200 bg-sky-soft/40 p-6 hidden md:block">
          <div className="overline mb-4">{t("admin.subtitle")}</div>
          <nav className="space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-xl ${isActive ? "bg-cobalt-500 text-white" : "text-ink-700 hover:bg-white"}`
                }
                data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {l.icon} {l.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="flex-1 min-w-0 p-6 lg:p-10">
          <div className="md:hidden -mx-2 mb-6 overflow-x-auto" data-testid="admin-mobile-nav">
            <nav className="flex gap-1.5 px-2 w-max">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-full whitespace-nowrap border ${isActive ? "bg-cobalt-500 border-cobalt-500 text-white" : "border-ink-200 bg-white text-ink-700"}`
                  }
                  data-testid={`admin-mobile-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {l.icon} {l.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
